import {
  ORDER_STATUS_COLORS,
  ASSIGNMENT_STATUS_COLORS,
  PAYMENT_STATUS,
} from "../constants/dashboard.constants";

// ─── Orders ───────────────────────────────────────────────────────────────────

/**
 * Given a single order document, returns the most recent currentStatus string.
 * The currentStatus lives inside orderDetails.orderStatus (array of objects),
 * and we want the LAST element's currentStatus property.
 */
export const getLatestOrderStatus = (order) => {
  try {
    const statusArray = order?.orderDetails?.orderStatus;
    if (!Array.isArray(statusArray) || statusArray.length === 0) return null;
    return statusArray[statusArray.length - 1]?.currentStatus ?? null;
  } catch {
    return null;
  }
};

/**
 * Traverses all order documents and builds a sorted array of
 * { status, count, color } objects — one entry per distinct status.
 *
 * @param {Object[]} orders – raw Firestore order documents
 * @returns {{ status: string; count: number; color: string }[]}
 */
export const aggregateOrderStatuses = (orders) => {
  const countMap = {};

  orders.forEach((order) => {
    const status = getLatestOrderStatus(order);
    if (!status) return;
    const key = status.toLowerCase().trim();
    countMap[key] = (countMap[key] ?? 0) + 1;
  });

  return Object.entries(countMap)
    .map(([status, count]) => ({
      status,
      count,
      color:
        ORDER_STATUS_COLORS[status] ?? ORDER_STATUS_COLORS.default,
    }))
    .sort((a, b) => b.count - a.count);
};

// ─── Assignments ──────────────────────────────────────────────────────────────

/**
 * Traverses all assignment documents and builds a warehouse-keyed map:
 *
 * {
 *   "Warehouse A": [{ status, count, color }, …],
 *   "Warehouse B": [{ status, count, color }, …],
 * }
 *
 * @param {Object[]} assignments – raw Firestore assignment documents
 * @returns {Record<string, { status: string; count: number; color: string }[]>}
 */
export const aggregateAssignmentsByWarehouse = (assignments) => {
  // Step 1 – build nested count map: { warehouse -> { status -> count } }
  const nestedMap = {};

  assignments.forEach((asgn) => {
    const warehouse = asgn?.assignedTo?.trim() || "Unknown";
    const status = asgn?.currentStatus?.toLowerCase().trim();
    if (!status) return;

    if (!nestedMap[warehouse]) nestedMap[warehouse] = {};
    nestedMap[warehouse][status] = (nestedMap[warehouse][status] ?? 0) + 1;
  });

  // Step 2 – convert to the desired output shape
  const result = {};
  Object.entries(nestedMap).forEach(([warehouse, statusMap]) => {
    result[warehouse] = Object.entries(statusMap)
      .map(([status, count]) => ({
        status,
        count,
        color:
          ASSIGNMENT_STATUS_COLORS[status] ??
          ASSIGNMENT_STATUS_COLORS.default,
      }))
      .sort((a, b) => b.count - a.count);
  });

  return result;
};

// ─── Payments ─────────────────────────────────────────────────────────────────

/**
 * For a single payment document, sums all amountPaid values in the
 * payments array and returns the total amount paid.
 *
 * @param {Object} paymentDoc
 * @returns {number}
 */
export const sumAmountPaid = (paymentDoc) => {
  const arr = paymentDoc?.payments;
  if (!Array.isArray(arr) || arr.length === 0) return 0;
  return arr.reduce((acc, p) => acc + (Number(p?.amountPaid) || 0), 0);
};

/**
 * Classifies a single payment document as fully paid, partially paid,
 * or pending.
 *
 * @param {Object} paymentDoc
 * @returns {'fullyPaid' | 'partiallyPaid' | 'pending'}
 */
export const classifyPayment = (paymentDoc) => {
  const totalBilled = Number(paymentDoc?.totalBilledAmount) || 0;
  const totalPaid = sumAmountPaid(paymentDoc);

  if (totalPaid === 0) return PAYMENT_STATUS.PENDING;
  if (totalPaid >= totalBilled) return PAYMENT_STATUS.FULLY_PAID;
  return PAYMENT_STATUS.PARTIALLY_PAID;
};

/**
 * Aggregates all payment documents into counts and monetary totals.
 *
 * Returns:
 * {
 *   counts: { fullyPaid, partiallyPaid, pending },
 *   totals: { totalBilledAmount, totalAmountReceived, totalPendingAmount },
 * }
 *
 * @param {Object[]} payments – raw Firestore payment documents
 */
export const aggregatePayments = (payments) => {
  const counts = {
    [PAYMENT_STATUS.FULLY_PAID]: 0,
    [PAYMENT_STATUS.PARTIALLY_PAID]: 0,
    [PAYMENT_STATUS.PENDING]: 0,
  };

  let totalBilledAmount = 0;
  let totalAmountReceived = 0;
  let totalPendingAmount = 0;

  payments.forEach((paymentDoc) => {
    const classification = classifyPayment(paymentDoc);
    counts[classification] += 1;

    const billed = Number(paymentDoc?.totalBilledAmount) || 0;
    const paid = sumAmountPaid(paymentDoc);
    const pending = Math.max(0, billed - paid);

    totalBilledAmount += billed;
    totalAmountReceived += paid;
    totalPendingAmount += pending;
  });

  return {
    counts,
    totals: {
      totalBilledAmount,
      totalAmountReceived,
      totalPendingAmount,
    },
  };
};

// ─── Formatting helpers ───────────────────────────────────────────────────────

/**
 * Formats a number as Indian currency (₹ with Indian comma grouping).
 * Falls back to a plain number string if formatting is unavailable.
 *
 * @param {number} amount
 * @returns {string}
 */
export const formatCurrency = (amount) => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `₹${amount.toLocaleString()}`;
  }
};

/**
 * Capitalises the first letter and replaces hyphens/underscores with spaces.
 * Used to pretty-print raw status strings.
 *
 * @param {string} str
 * @returns {string}
 */
export const prettifyStatus = (str = "") =>
  str
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
